<template>
  <div class="min-h-screen bg-gradient-to-b from-[#142a4a] via-[#18365f] to-[#1b4475]">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 class="text-3xl font-bold text-slate-100">APIs externas</h1>
          <p class="text-slate-400 mt-1 text-sm">
            Cadastro e parametrização de APIs e endpoints para uso em agentes.
          </p>
        </div>
        <button
          type="button"
          class="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 text-sm font-semibold transition"
          @click="openApiModal(null)"
        >
          Nova API
        </button>
      </div>

      <div v-if="loadError" class="mb-4 p-4 bg-red-500/10 border border-red-400/40 text-red-300 rounded-lg text-sm">
        {{ loadError }}
      </div>

      <div v-if="loading" class="text-center py-12 text-slate-400">Carregando…</div>

      <ul v-else class="space-y-4">
        <li
          v-for="api in apis"
          :key="api.id"
          class="rounded-xl border-2 border-cyan-400/35 bg-slate-900/85 backdrop-blur-sm p-5 hover:border-cyan-400/70 transition"
        >
          <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <h2 class="text-lg font-semibold text-slate-100">{{ api.name }}</h2>
                <span
                  class="text-xs px-2 py-0.5 rounded-full border"
                  :class="api.active ? 'border-emerald-400/40 text-emerald-300 bg-emerald-500/10' : 'border-slate-600 text-slate-400'"
                >
                  {{ api.active ? 'Ativa' : 'Inativa' }}
                </span>
                <span v-if="api.bearer_token_configured" class="text-xs text-emerald-300/90">Token configurado</span>
                <span v-else class="text-xs text-amber-300/90">Sem token</span>
                <span class="text-xs px-2 py-0.5 rounded-full border border-cyan-400/35 text-cyan-200 bg-cyan-500/10">
                  {{ api.api_kind === 'graphql' ? 'GraphQL' : 'REST' }}
                </span>
              </div>
              <p class="text-sm font-mono text-slate-400 mt-1 break-all">{{ api.base_url }}</p>
              <p class="text-xs text-slate-500 mt-1">{{ api.endpoints_count }} endpoint(s)</p>
            </div>
            <div class="flex flex-wrap gap-2">
              <button
                type="button"
                class="px-3 py-1.5 text-sm rounded-lg border border-slate-500/60 text-slate-200 hover:bg-slate-800/80"
                @click="toggleExpand(api.id)"
              >
                {{ expandedId === api.id ? 'Ocultar endpoints' : 'Visualizar endpoints' }}
              </button>
              <button
                type="button"
                class="px-3 py-1.5 text-sm rounded-lg border border-cyan-400/45 text-cyan-200 hover:bg-cyan-400/12"
                @click="openApiModal(api.id)"
              >
                Editar
              </button>
              <button
                type="button"
                class="px-3 py-1.5 text-sm rounded-lg border border-red-400/45 text-red-200 hover:bg-red-500/20"
                @click="openApiDeleteConfirm(api)"
              >
                Excluir
              </button>
            </div>
          </div>

          <div v-if="expandedId === api.id" class="mt-4 pt-4 border-t border-slate-700/70">
            <div v-if="detailLoading" class="text-slate-400 text-sm py-4">Carregando detalhes…</div>
            <template v-else-if="detail && detail.id === api.id">
              <div class="flex justify-between items-center mb-3">
                <h3 class="text-sm font-medium text-slate-300">Endpoints</h3>
                <button
                  type="button"
                  class="text-sm px-3 py-1 rounded-lg bg-[#2AD7DE]/20 text-cyan-200 border border-cyan-400/40 hover:bg-[#2AD7DE]/30"
                  @click="openEndpointModal(api.id, null)"
                >
                  + Endpoint
                </button>
              </div>
              <ul v-if="detail.endpoints.length" class="space-y-2">
                <li
                  v-for="ep in detail.endpoints"
                  :key="ep.id"
                  class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-slate-600/60 bg-slate-950/40 p-3 text-sm"
                >
                  <div class="min-w-0">
                    <div class="font-medium text-slate-200">
                      <template v-if="detail.api_kind === 'graphql'">
                        <span class="font-mono text-fuchsia-300/90">GRAPHQL</span>
                        <span class="text-slate-300">operation:</span>
                        <span class="font-mono text-fuchsia-200">{{ ep.graphql_operation_name || '—' }}</span>
                      </template>
                      <template v-else>
                        <span class="font-mono text-cyan-300/90">{{ ep.http_method }}</span>
                        {{ ep.path_template }}
                      </template>
                    </div>
                    <div v-if="detail.api_kind !== 'graphql'" class="text-xs mt-1">
                      <span
                        class="inline-flex items-center px-2 py-0.5 rounded border"
                        :class="
                          ep.endpoint_intent === 'write'
                            ? 'border-amber-400/40 text-amber-200 bg-amber-400/10'
                            : 'border-emerald-400/40 text-emerald-200 bg-emerald-400/10'
                        "
                      >
                        {{ ep.endpoint_intent === 'write' ? 'Gravação' : 'Leitura' }}
                      </span>
                    </div>
                    <div class="text-slate-400">{{ ep.name }} · <span class="font-mono text-slate-300">{{ ep.operation_key }}</span></div>
                    <div v-if="(ep.parameter_schema || []).length" class="text-xs text-slate-500 mt-1">
                      Parâmetros:
                      <span v-for="(p, i) in ep.parameter_schema" :key="i">
                        {{ p.name }} ({{ p.in }}{{ p.fixed ? ', fixo' : '' }})<span v-if="i < ep.parameter_schema.length - 1">, </span>
                      </span>
                    </div>
                  </div>
                  <div class="flex gap-2">
                    <button
                      type="button"
                      class="px-2 py-1 rounded border border-slate-500 text-slate-300 hover:bg-slate-800 text-xs"
                      @click="openEndpointModal(api.id, ep.id)"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      class="px-2 py-1 rounded border border-red-400/40 text-red-300 hover:bg-red-500/15 text-xs"
                      @click="openEndpointDeleteConfirm(api.id, ep)"
                    >
                      Excluir
                    </button>
                  </div>
                </li>
              </ul>
              <p v-else class="text-slate-500 text-sm">Nenhum endpoint. Adicione ao menos um para usar na orquestração.</p>
            </template>
          </div>
        </li>
      </ul>

      <div v-if="!loading && apis.length === 0" class="text-center py-12 text-slate-400">
        Nenhuma API cadastrada. Clique em “Nova API”.
      </div>
    </div>

    <!-- Modal API (layout alinhado ao modal de agentes) -->
    <div
      v-if="apiModalOpen"
      class="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-950/50 backdrop-blur-sm"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div class="px-6 py-4 bg-[#2f4563] border-b border-cyan-200/25 flex justify-between items-start gap-3 flex-shrink-0">
          <h3 class="text-lg font-semibold text-slate-100">{{ editingApiId ? 'Editar API' : 'Nova API' }}</h3>
          <button
            type="button"
            class="text-slate-500 hover:text-cyan-300 p-1 -mr-1 transition"
            aria-label="Fechar"
            @click="closeApiModal"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form class="flex flex-col flex-1 min-h-0" @submit.prevent="openApiSaveConfirm">
          <div
            class="px-6 py-4 overflow-y-auto flex-1 min-h-0 [scrollbar-width:thin] [scrollbar-color:rgba(103,232,249,0.45)_rgba(47,69,99,0.4)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#2f4563]/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-cyan-300/45 [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-[#2f4563]/80 [&::-webkit-scrollbar-thumb:hover]:bg-cyan-300/70"
          >
            <div v-if="apiModalLoading" class="py-16 text-center text-slate-400 text-sm">
              Carregando dados da API…
            </div>
            <div v-else class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Nome <span class="text-red-400">*</span></label>
                <input
                  v-model="apiForm.name"
                  required
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">URL base <span class="text-red-400">*</span></label>
                <input
                  v-model="apiForm.base_url"
                  type="url"
                  required
                  placeholder="https://api.exemplo.com/v1"
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Tipo da API <span class="text-red-400">*</span></label>
                <select
                  v-model="apiForm.api_kind"
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100"
                >
                  <option value="rest" class="bg-slate-900">REST</option>
                  <option value="graphql" class="bg-slate-900">GraphQL</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Token Bearer (opcional)</label>
                <input
                  v-model="apiForm.bearer_token"
                  type="password"
                  autocomplete="off"
                  :placeholder="apiFormTokenConfigured ? '•••••••• deixe em branco para manter' : 'Cole o token'"
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
              </div>
              <label class="flex items-start gap-2 text-sm text-slate-300">
                <input v-model="apiForm.clear_bearer_token" type="checkbox" class="mt-1 rounded border-slate-500 bg-slate-800 text-cyan-400" />
                Remover token armazenado
              </label>
              <label class="flex items-center gap-2 text-sm text-slate-300">
                <input v-model="apiForm.active" type="checkbox" class="rounded border-slate-500 bg-slate-800 text-cyan-400" />
                API ativa
              </label>
            </div>
          </div>
          <div class="px-6 py-4 border-t border-slate-600/70 flex flex-col gap-3 flex-shrink-0 bg-slate-700/55">
            <div
              v-if="apiModalError && !apiModalLoading"
              role="alert"
              class="relative w-full rounded-lg border border-red-400/50 bg-red-500/15 pl-3 pr-10 py-2.5 text-sm text-red-100 leading-snug"
            >
              <button
                type="button"
                class="absolute top-2 right-2 p-1 rounded-md text-red-200/90 hover:text-red-50 hover:bg-red-500/25 transition leading-none"
                aria-label="Fechar aviso"
                @click="dismissApiModalError"
              >
                <span class="text-lg font-light" aria-hidden="true">×</span>
              </button>
              <span class="font-semibold text-red-50 block mb-1 pr-1">Não foi possível salvar.</span>
              <span class="text-red-100/95 break-words pr-1">{{ apiModalError }}</span>
            </div>
            <div class="flex justify-end gap-2">
              <button
                type="submit"
                :disabled="apiSaving || apiModalLoading"
                class="px-4 py-2 text-sm rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ apiSaving ? 'Salvando…' : 'Salvar' }}
              </button>
              <button
                type="button"
                class="px-4 py-2 text-sm rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800/70"
                @click="closeApiModal"
              >
                Cancelar
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Confirmação salvar API (padrão Agentes) -->
    <div
      v-if="apiSaveConfirmOpen"
      class="fixed inset-0 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      @click.self="closeApiSaveConfirm"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-md w-full p-6">
        <h3 class="flex items-center text-lg font-semibold text-slate-100 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z"
              clip-rule="evenodd"
            />
          </svg>
          Confirmação!
        </h3>
        <p class="text-slate-300 mb-6">Deseja salvar esta API?</p>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            :disabled="apiSaving"
            class="px-4 py-2 bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold rounded-lg transition disabled:opacity-50"
            @click="confirmApiSave"
          >
            {{ apiSaving ? 'Salvando…' : 'Sim' }}
          </button>
          <button
            type="button"
            class="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800/70 transition"
            @click="closeApiSaveConfirm"
          >
            Não
          </button>
        </div>
      </div>
    </div>

    <!-- Confirmação excluir API (padrão Agentes) -->
    <div
      v-if="apiDeleteConfirm"
      class="fixed inset-0 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      @click.self="closeApiDeleteConfirm"
    >
      <div
        class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-2xl p-6 max-h-[90vh] overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(103,232,249,0.45)_rgba(47,69,99,0.4)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#2f4563]/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-cyan-300/45 [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-[#2f4563]/80 [&::-webkit-scrollbar-thumb:hover]:bg-cyan-300/70"
      >
        <h3 class="flex items-center text-lg font-semibold text-slate-100 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z"
              clip-rule="evenodd"
            />
          </svg>
          Confirmação!
        </h3>
        <p class="text-slate-300 mb-6">
          Tem certeza que deseja excluir a API <strong class="text-slate-100">{{ apiDeleteConfirm.name }}</strong> e todos os
          {{ apiDeleteConfirm.endpoints_count ?? 0 }} endpoint(s)?
          <span class="block mt-2 font-mono text-sm text-slate-400 break-all">{{ apiDeleteConfirm.base_url }}</span>
          <span class="block mt-1 text-sm">Esta ação não pode ser desfeita.</span>
        </p>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            :disabled="apiDeleting"
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition disabled:opacity-50"
            @click="executeApiDelete"
          >
            {{ apiDeleting ? 'Excluindo…' : 'Sim' }}
          </button>
          <button
            type="button"
            class="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800/70 transition"
            @click="closeApiDeleteConfirm"
          >
            Não
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Endpoint (layout alinhado ao modal de agentes) -->
    <div
      v-if="endpointModalOpen"
      class="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-slate-950/50 backdrop-blur-sm"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div class="px-6 py-4 bg-[#2f4563] border-b border-cyan-200/25 flex justify-between items-start gap-3 flex-shrink-0">
          <h3 class="text-lg font-semibold text-slate-100">{{ editingEndpointId ? 'Editar endpoint' : 'Novo endpoint' }}</h3>
          <button
            type="button"
            class="text-slate-500 hover:text-cyan-300 p-1 -mr-1 transition"
            aria-label="Fechar"
            @click="closeEndpointModal"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form class="flex flex-col flex-1 min-h-0" @submit.prevent="openEndpointSaveConfirm">
          <div
            class="px-6 py-4 overflow-y-auto flex-1 min-h-0 [scrollbar-width:thin] [scrollbar-color:rgba(103,232,249,0.45)_rgba(47,69,99,0.4)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#2f4563]/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-cyan-300/45 [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-[#2f4563]/80 [&::-webkit-scrollbar-thumb:hover]:bg-cyan-300/70"
          >
            <div v-if="endpointModalLoading" class="py-16 text-center text-slate-400 text-sm">
              Carregando dados do endpoint…
            </div>
            <div v-else class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Nome amigável <span class="text-red-400">*</span></label>
                <input
                  v-model="endpointForm.name"
                  required
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Chave da operação <span class="text-red-400">*</span></label>
                <input
                  v-model="endpointForm.operation_key"
                  required
                  placeholder="Ex.: Listar pedidos ou listar_pedidos"
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
                <p class="text-xs text-slate-300 mt-1 leading-snug">
                  Identificador técnico da operação. Ajuda em logs, rastreabilidade e integrações com outros sistemas.
                </p>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Descrição do Endpoint</label>
                <textarea
                  v-model="endpointForm.description"
                  rows="2"
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70 text-sm"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Instruções para o modelo (LLM)</label>
                <textarea
                  v-model="endpointForm.llm_instructions"
                  rows="4"
                  placeholder="Regras de preenchimento de parâmetros, sinónimos, formatos de data, etc. Incluídas no chat junto ao prompt do agente."
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70 text-sm"
                />
                <p class="text-xs text-slate-400 mt-1 leading-snug">
                  Texto opcional só para o modelo (não substitui o schema). Máx. 20&nbsp;000 caracteres.
                </p>
              </div>
              <div v-if="!endpointParentIsGraphql" class="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-slate-300 mb-1">Método <span class="text-red-400">*</span></label>
                  <select
                    v-model="endpointForm.http_method"
                    class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100"
                  >
                    <option v-for="m in httpMethods" :key="m" :value="m" class="bg-slate-900">{{ m }}</option>
                  </select>
                </div>
                <div class="md:col-span-3">
                  <label class="block text-sm font-medium text-slate-300 mb-1">Tipo do método <span class="text-red-400">*</span></label>
                  <select
                    v-model="endpointForm.endpoint_intent"
                    class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100"
                    :disabled="endpointForm.http_method === 'GET'"
                  >
                    <option v-for="opt in endpointIntentOptions" :key="opt.value" :value="opt.value" class="bg-slate-900">
                      {{ opt.label }}
                    </option>
                  </select>
                </div>
                <div class="md:col-span-7">
                  <label class="block text-sm font-medium text-slate-300 mb-1">Path <span class="text-red-400">*</span></label>
                  <input
                    v-model="endpointForm.path_template"
                    required
                    placeholder="pedidos ou pedidos/:id"
                    class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                  />
                </div>
              </div>
              <div v-else class="space-y-3 rounded-lg border border-fuchsia-400/25 bg-slate-950/35 p-3">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label class="block text-sm font-medium text-slate-300 mb-1">Modo de variáveis</label>
                    <select
                      v-model="endpointForm.graphql_variables_mode"
                      class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100"
                    >
                      <option value="rows" class="bg-slate-900">Por variável</option>
                      <option value="json" class="bg-slate-900">JSON completo</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-slate-300 mb-1">Operation name (opcional)</label>
                    <input
                      v-model="endpointForm.graphql_operation_name"
                      placeholder="Ex.: CompanyCreate"
                      class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                    />
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-1">Query / Mutation GraphQL <span class="text-red-400">*</span></label>
                  <textarea
                    v-model="endpointForm.graphql_query"
                    rows="6"
                    required
                    placeholder="mutation companyCreate($company: CompanyInput!) { companyCreate(company: $company) { id name } }"
                    class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                  />
                </div>
                <div v-if="endpointForm.graphql_variables_mode === 'json'">
                  <label class="block text-sm font-medium text-slate-300 mb-1">Variables JSON</label>
                  <textarea
                    v-model="endpointForm.graphql_variables_json"
                    rows="8"
                    placeholder='{"cnpj":"88581181000138","params":{"name":"Teste"}}'
                    class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                  />
                </div>
                <label class="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    v-model="endpointForm.prune_empty_fields"
                    type="checkbox"
                    class="rounded border-slate-500 bg-slate-800 text-cyan-400"
                  />
                  Limpar campos vazios antes de enviar
                </label>
              </div>
              <div v-if="!endpointParentIsGraphql || endpointForm.graphql_variables_mode === 'rows'">
                <div class="flex justify-between items-center mb-2">
                  <label class="text-sm font-medium text-slate-300">
                    {{ endpointParentIsGraphql ? 'Variáveis GraphQL (enviadas em variables)' : 'Parâmetros (mínimos esperados)' }}
                  </label>
                  <button type="button" class="text-xs text-cyan-300 hover:text-cyan-200 font-medium" @click="addParamRow">
                    + Parâmetro
                  </button>
                </div>

                <div
                  v-for="(row, idx) in endpointForm.parameter_schema"
                  :key="idx"
                  class="mb-2 rounded-lg border border-slate-600/50 bg-slate-950/30 p-2"
                >
                  <div class="grid grid-cols-12 gap-2 items-end">
                    <input
                      v-model="row.name"
                      placeholder="nome"
                      class="col-span-12 sm:col-span-4 min-w-0 px-2 py-1.5 rounded border border-slate-600 bg-slate-900/60 text-slate-100 text-xs font-mono"
                    />
                    <select
                      v-model="row.in"
                      class="col-span-6 min-w-0 px-2 py-1.5 rounded border border-slate-600 bg-slate-900/60 text-slate-100 text-xs sm:col-span-2"
                      :disabled="endpointParentIsGraphql"
                    >
                      <option value="" class="bg-slate-900">—</option>
                      <option v-if="!endpointParentIsGraphql" value="path" class="bg-slate-900">path</option>
                      <option v-if="!endpointParentIsGraphql" value="query" class="bg-slate-900">query</option>
                      <option value="body" class="bg-slate-900">body</option>
                    </select>
                    <select
                      v-model="row.type"
                      class="col-span-6 min-w-0 px-2 py-1.5 rounded border border-slate-600 bg-slate-900/60 text-slate-100 text-xs sm:col-span-2"
                    >
                      <option v-for="t in paramTypes" :key="t" :value="t" class="bg-slate-900">{{ t }}</option>
                    </select>
                    <input
                      v-model="row.test_value"
                      type="text"
                      autocomplete="off"
                      :placeholder="row.fixed ? 'Valor fixo (sempre enviado)' : 'Valor p/ teste'"
                      class="col-span-12 sm:col-span-4 min-w-0 px-2 py-1.5 rounded border border-slate-600 bg-slate-900/60 text-slate-200 text-xs font-mono"
                    />
                  </div>
                  <div class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-700/40 pt-2">
                    <label class="flex items-center gap-2 text-xs text-slate-300">
                      <input v-model="row.required" type="checkbox" class="rounded border-slate-500" />
                      Obrigatório
                    </label>
                    <label
                      class="flex items-center gap-2 text-xs text-slate-300 cursor-pointer"
                      title="Envia sempre o valor do campo acima; o agente não preenche este parâmetro."
                    >
                      <input v-model="row.fixed" type="checkbox" class="rounded border-slate-500" />
                      Valor fixo
                    </label>
                    <button
                      type="button"
                      class="ml-auto inline-flex shrink-0 items-center justify-center rounded px-2 py-1 text-xs leading-none text-red-400 hover:bg-red-500/10"
                      title="Remover parâmetro"
                      @click="removeParamRow(idx)"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>
              <div class="rounded-lg border border-slate-600/50 bg-slate-950/30 p-3 space-y-2">
                <label class="block text-sm font-medium text-slate-300">Schema dos parâmetros (formato, obrigatório, exceções)</label>
                <p class="text-xs text-slate-500 leading-relaxed">
                  Opcional — REST, GraphQL e o mesmo formato nas views de BD. Alinhe as chaves de
                  <span class="font-mono text-slate-400">fields</span> aos nomes dos parâmetros (REST: nome do parâmetro; GraphQL: path da variável;
                  pode usar pontos, ex. <span class="font-mono">params.cnpj</span>).
                  <span class="font-mono text-slate-400">required_date_exceptions</span>: paths que, se preenchidos, isentam obrigatoriedade de datas com
                  <span class="font-mono">required: true</span>. Tipos: date, datetime, string, number, integer, boolean.
                </p>
                <textarea
                  v-model="endpointForm.graphql_variables_field_schema_json"
                  rows="10"
                  placeholder='{"fields":{"freight.service_date":{"format":"date_us","required":true}},"required_date_exceptions":["freight.cte_number"]}'
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
              </div>
              <div class="rounded-lg border border-cyan-400/25 bg-slate-950/45 p-3 space-y-3 text-xs">
                <h4 class="text-sm font-medium text-slate-200">Pré-visualização</h4>
                <div>
                  <p class="text-[10px] uppercase tracking-wide text-slate-300 mb-1">URL completa</p>
                  <p class="font-mono text-cyan-100/95 break-all text-[11px] leading-relaxed">{{ endpointPreviewFullUrl }}</p>
                </div>
                <div v-if="endpointPreviewShowJson">
                  <p class="text-[10px] uppercase tracking-wide text-slate-300 mb-1">
                    {{ endpointParentIsGraphql ? 'Payload GraphQL' : 'Corpo JSON (parâmetros em body)' }}
                  </p>
                  <pre
                    class="p-2 rounded bg-slate-900/85 text-slate-300 overflow-x-auto max-h-44 whitespace-pre-wrap font-mono text-[11px]"
                  >{{ endpointPreviewBodyJson }}</pre>
                </div>
                <p v-else class="text-slate-400 text-[11px] leading-snug">
                  Parâmetros em <span class="font-mono text-slate-300">query</span> entram na URL acima. Defina parâmetros em
                  <span class="font-mono text-slate-300">body</span> para ver o JSON; «Testar requisição» envia esse JSON no corpo
                  (<span class="font-mono text-cyan-200/90">GET</span> ou <span class="font-mono text-cyan-200/90">POST</span>,
                  conforme o método escolhido).
                </p>
              </div>
              <div
                v-if="endpointTestResult"
                class="rounded-lg border border-slate-600/70 bg-slate-950/50 p-3 text-xs space-y-2"
              >
                <div class="flex flex-wrap items-center gap-2 text-slate-200">
                  <span
                    class="px-2 py-0.5 rounded font-mono"
                    :class="endpointTestResult.http_ok ? 'bg-emerald-500/20 text-emerald-200' : 'bg-amber-500/20 text-amber-100'"
                  >
                    HTTP {{ endpointTestResult.status }}
                  </span>
                  <span v-if="endpointTestResult.content_type" class="text-slate-500">{{ endpointTestResult.content_type }}</span>
                </div>
                <p class="text-slate-500 break-all font-mono text-[11px]">{{ endpointTestResult.request_url }}</p>
                <pre class="mt-1 p-2 rounded bg-slate-900/80 text-slate-300 overflow-x-auto max-h-48 whitespace-pre-wrap">{{ formatEndpointTestBody(endpointTestResult) }}</pre>
              </div>
            </div>
          </div>
          <div
            class="px-6 py-4 border-t border-slate-600/70 flex flex-col gap-3 flex-shrink-0 bg-slate-700/55"
          >
            <div
              v-if="endpointModalError && !endpointModalLoading"
              role="alert"
              class="relative w-full rounded-lg border border-red-400/50 bg-red-500/15 pl-3 pr-10 py-2.5 text-sm text-red-100 leading-snug"
            >
              <button
                type="button"
                class="absolute top-2 right-2 p-1 rounded-md text-red-200/90 hover:text-red-50 hover:bg-red-500/25 transition leading-none"
                aria-label="Fechar aviso"
                @click="dismissEndpointModalError"
              >
                <span class="text-lg font-light" aria-hidden="true">×</span>
              </button>
              <span class="font-semibold text-red-50 block mb-1 pr-1">Não foi possível salvar.</span>
              <span class="text-red-100/95 break-words pr-1">{{ endpointModalError }}</span>
            </div>
            <div
              v-if="endpointTestError"
              role="alert"
              class="relative w-full rounded-lg border border-red-400/50 bg-red-500/15 pl-3 pr-10 py-2.5 text-sm text-red-100 leading-snug"
            >
              <button
                type="button"
                class="absolute top-2 right-2 p-1 rounded-md text-red-200/90 hover:text-red-50 hover:bg-red-500/25 transition leading-none"
                aria-label="Fechar aviso"
                @click="dismissEndpointTestError"
              >
                <span class="text-lg font-light" aria-hidden="true">×</span>
              </button>
              <span class="font-semibold text-red-50 block mb-1 pr-1">Erro no teste.</span>
              <span class="text-red-100/95 break-words pr-1">{{ endpointTestError }}</span>
            </div>
            <div class="flex flex-wrap justify-between items-center gap-2">
              <button
                type="button"
                :disabled="endpointTestLoading || endpointModalLoading"
                class="px-4 py-2 text-sm rounded-lg border border-cyan-400/50 text-cyan-100 hover:bg-cyan-400/10 transition disabled:opacity-50 disabled:cursor-not-allowed"
                @click="testEndpoint"
              >
                {{ endpointTestLoading ? 'Testando…' : 'Testar requisição' }}
              </button>
              <div class="flex flex-wrap gap-2 justify-end">
                <button
                  type="submit"
                  :disabled="endpointSaving || endpointModalLoading"
                  class="px-4 py-2 text-sm rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {{ endpointSaving ? 'Salvando…' : 'Salvar' }}
                </button>
                <button
                  type="button"
                  class="px-4 py-2 text-sm rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800/70"
                  @click="closeEndpointModal"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Confirmação salvar endpoint (padrão Agentes) -->
    <div
      v-if="endpointSaveConfirmOpen"
      class="fixed inset-0 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      @click.self="closeEndpointSaveConfirm"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-md w-full p-6">
        <h3 class="flex items-center text-lg font-semibold text-slate-100 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z"
              clip-rule="evenodd"
            />
          </svg>
          Confirmação!
        </h3>
        <p class="text-slate-300 mb-6">Deseja salvar este endpoint?</p>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            :disabled="endpointSaving"
            class="px-4 py-2 bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold rounded-lg transition disabled:opacity-50"
            @click="confirmEndpointSave"
          >
            {{ endpointSaving ? 'Salvando…' : 'Sim' }}
          </button>
          <button
            type="button"
            class="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800/70 transition"
            @click="closeEndpointSaveConfirm"
          >
            Não
          </button>
        </div>
      </div>
    </div>

    <!-- Confirmação excluir endpoint (padrão Agentes) -->
    <div
      v-if="endpointDeleteConfirm"
      class="fixed inset-0 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      @click.self="closeEndpointDeleteConfirm"
    >
      <div
        class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-2xl p-6 max-h-[90vh] overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(103,232,249,0.45)_rgba(47,69,99,0.4)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#2f4563]/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-cyan-300/45 [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-[#2f4563]/80 [&::-webkit-scrollbar-thumb:hover]:bg-cyan-300/70"
      >
        <h3 class="flex items-center text-lg font-semibold text-slate-100 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z"
              clip-rule="evenodd"
            />
          </svg>
          Confirmação!
        </h3>
        <p class="text-slate-300 mb-6">
          Tem certeza que deseja excluir o endpoint
          <strong class="text-slate-100">{{ endpointDeleteConfirm.ep.name }}</strong>?
          <span class="block mt-2 font-mono text-sm text-slate-400">
            {{
              endpointParentIsGraphql
                ? `GRAPHQL ${endpointDeleteConfirm.ep.graphql_operation_name || ''}`.trim()
                : `${endpointDeleteConfirm.ep.http_method} ${endpointDeleteConfirm.ep.path_template || ''}`.trim()
            }}
          </span>
          <span class="block mt-1 text-sm">Esta ação não pode ser desfeita.</span>
        </p>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            :disabled="endpointDeleting"
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition disabled:opacity-50"
            @click="executeEndpointDelete"
          >
            {{ endpointDeleting ? 'Excluindo…' : 'Sim' }}
          </button>
          <button
            type="button"
            class="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800/70 transition"
            @click="closeEndpointDeleteConfirm"
          >
            Não
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useApi } from '../composables/useApi.js'

const nonemptyParamRows = (schema) =>
  (schema || []).filter((r) => r.name && String(r.name).trim())

function normalizePreviewBaseUrl(url) {
  if (url == null || url === '') return ''
  return String(url).trim().replace(/\/+$/, '')
}

function normalizePreviewPathSegments(path) {
  if (path == null || path === '') return ''
  return String(path).trim().replace(/^\/+/, '').replace(/\/+/g, '/')
}

function formatGraphqlFieldSchemaJsonForForm(raw) {
  const o = raw && typeof raw === 'object' ? raw : {}
  const fields = o.fields && typeof o.fields === 'object' ? o.fields : {}
  const exc = Array.isArray(o.required_date_exceptions) ? o.required_date_exceptions : []
  return JSON.stringify({ fields, required_date_exceptions: exc }, null, 2)
}

function defaultGraphqlFieldSchemaJson() {
  return JSON.stringify({ fields: {}, required_date_exceptions: [] }, null, 2)
}

const emptyParamRow = () => ({ name: '', in: 'path', type: 'string', required: true, fixed: false, test_value: '' })

export default {
  name: 'ApiConnectors',
  data() {
    return {
      apis: [],
      loading: true,
      loadError: '',
      expandedId: null,
      detail: null,
      detailLoading: false,
      apiModalOpen: false,
      editingApiId: null,
      apiForm: {
        name: '',
        base_url: '',
        api_kind: 'rest',
        bearer_token: '',
        clear_bearer_token: false,
        active: true
      },
      apiFormTokenConfigured: false,
      apiSaving: false,
      apiModalLoading: false,
      apiModalError: '',
      apiSaveConfirmOpen: false,
      apiDeleteConfirm: null,
      apiDeleting: false,
      endpointModalOpen: false,
      endpointParentApiId: null,
      editingEndpointId: null,
      endpointForm: {
        name: '',
        operation_key: '',
        http_method: 'GET',
        endpoint_intent: 'read',
        path_template: '',
        graphql_query: '',
        graphql_operation_name: '',
        graphql_variables_mode: 'rows',
        graphql_variables_json: '',
        graphql_variables_field_schema_json: defaultGraphqlFieldSchemaJson(),
        prune_empty_fields: true,
        description: '',
        llm_instructions: '',
        parameter_schema: []
      },
      endpointSaving: false,
      endpointModalLoading: false,
      endpointModalError: '',
      endpointTestLoading: false,
      endpointTestResult: null,
      endpointTestError: '',
      endpointSaveConfirmOpen: false,
      endpointDeleteConfirm: null,
      endpointDeleting: false,
      httpMethods: ['GET', 'POST'],
      endpointIntentOptions: [
        { value: 'read', label: 'Leitura' },
        { value: 'write', label: 'Gravação' }
      ],
      paramTypes: ['string', 'number', 'integer', 'boolean', 'date', 'datetime', 'date_range']
    }
  },
  mounted() {
    this.loadApis()
  },
  computed: {
    endpointParentApiBaseUrl() {
      const id = this.endpointParentApiId
      if (!id) return ''
      if (this.detail?.id === id && this.detail.base_url) return this.detail.base_url
      const row = this.apis.find((a) => a.id === id)
      return (row && row.base_url) || ''
    },
    endpointParentApiKind() {
      const id = this.endpointParentApiId
      if (!id) return 'rest'
      if (this.detail?.id === id && this.detail.api_kind) return this.detail.api_kind
      const row = this.apis.find((a) => a.id === id)
      return row?.api_kind || 'rest'
    },
    endpointParentIsGraphql() {
      return this.endpointParentApiKind === 'graphql'
    },
    endpointPreviewFullUrl() {
      const base = normalizePreviewBaseUrl(this.endpointParentApiBaseUrl)
      if (!base) {
        return 'Indique a URL base da API no cadastro (ou expanda a API para carregar os detalhes).'
      }
      if (this.endpointParentIsGraphql) return base
      const pathTpl = (this.endpointForm.path_template || '').trim()
      if (!pathTpl) {
        return `${base}/… (preencha o path)`
      }
      const pathMap = this.paramValuesByLocation('path')
      let path = String(this.endpointForm.path_template || '').replace(/:(\w+)/g, (_, key) => {
        const raw = pathMap[key]
        if (raw == null || String(raw).trim() === '') return `:${key}`
        return encodeURIComponent(String(raw).trim())
      })
      path = normalizePreviewPathSegments(path)
      let url = `${base}/${path}`
      const pairs = []
      for (const r of nonemptyParamRows(this.endpointForm.parameter_schema)) {
        const inn = r.in || ''
        const name = String(r.name).trim()
        const val = String(r.test_value ?? '').trim()
        if (!val) continue
        if (inn === 'query') pairs.push([name, val])
      }
      if (pairs.length) {
        const usp = new URLSearchParams()
        for (const [k, v] of pairs) usp.append(k, v)
        url += `?${usp.toString()}`
      }
      return url
    },
    endpointPreviewShowJson() {
      if (this.endpointParentIsGraphql) return true
      return nonemptyParamRows(this.endpointForm.parameter_schema).some((r) => (r.in || '') === 'body')
    },
    endpointPreviewBodyJson() {
      if (!this.endpointPreviewShowJson) return ''
      try {
        if (this.endpointParentIsGraphql) {
          return JSON.stringify(this.buildPreviewGraphqlPayload(), null, 2)
        }
        const obj = this.buildPreviewBodyObject()
        return JSON.stringify(obj, null, 2)
      } catch (e) {
        return `Erro ao montar o JSON: ${e.message || e}`
      }
    }
  },
  watch: {
    'endpointForm.http_method'(method) {
      if (this.endpointParentIsGraphql) return
      if (String(method || '').toUpperCase() === 'GET') {
        this.endpointForm.endpoint_intent = 'read'
      }
    },
    endpointParentIsGraphql(isGraphql) {
      if (!isGraphql) return
      this.endpointForm.http_method = 'POST'
      this.endpointForm.endpoint_intent = 'read'
      this.endpointForm.graphql_variables_mode = this.endpointForm.graphql_variables_mode || 'rows'
      this.endpointForm.parameter_schema = (this.endpointForm.parameter_schema || []).map((r) => ({
        ...r,
        in: 'body'
      }))
    }
  },
  methods: {
    paramValuesByLocation(location) {
      const m = {}
      for (const r of nonemptyParamRows(this.endpointForm.parameter_schema)) {
        if ((r.in || '') !== location) continue
        m[String(r.name).trim()] = r.test_value
      }
      return m
    },
    coercePreviewValue(raw, type) {
      const s = String(raw)
      switch (type) {
        case 'integer': {
          const n = parseInt(s, 10)
          return Number.isNaN(n) ? raw : n
        }
        case 'number': {
          const n = parseFloat(s)
          return Number.isNaN(n) ? raw : n
        }
        case 'boolean':
          return s === 'true' || s === '1' || s.toLowerCase() === 'yes'
        case 'date_range':
        case 'date':
        case 'datetime':
          return raw
        default:
          return raw
      }
    },
    deepAssignBodyPreview(root, dottedPath, value) {
      const parts = dottedPath
        .split('.')
        .map((x) => x.trim())
        .filter(Boolean)
      if (!parts.length) throw new Error(`Nome inválido: «${dottedPath}»`)
      let cursor = root
      for (let i = 0; i < parts.length - 1; i++) {
        const seg = parts[i]
        const child = cursor[seg]
        if (child == null) {
          cursor[seg] = {}
          cursor = cursor[seg]
        } else if (typeof child === 'object' && child !== null && !Array.isArray(child)) {
          cursor = child
        } else {
          throw new Error(`Conflito: «${dottedPath}» — o segmento «${seg}» já é um valor simples.`)
        }
      }
      const leaf = parts[parts.length - 1]
      const existing = cursor[leaf]
      if (
        existing != null &&
        typeof existing === 'object' &&
        !Array.isArray(existing) &&
        Object.keys(existing).length > 0
      ) {
        throw new Error(`Conflito: «${dottedPath}» — «${leaf}» já contém sub-objetos.`)
      }
      cursor[leaf] = value
    },
    buildPreviewBodyObject() {
      const out = {}
      for (const r of nonemptyParamRows(this.endpointForm.parameter_schema)) {
        if ((r.in || '') !== 'body') continue
        const n = String(r.name).trim()
        const val = String(r.test_value ?? '').trim()
        if (!val) continue
        this.deepAssignBodyPreview(out, n, this.coercePreviewValue(val, r.type || 'string'))
      }
      return out
    },
    buildPreviewGraphqlPayload() {
      const variables = this.graphqlVariablesTemplateObject()
      const payload = {
        query: (this.endpointForm.graphql_query || '').trim(),
        variables
      }
      const op = (this.endpointForm.graphql_operation_name || '').trim()
      if (op) payload.operationName = op
      return payload
    },
    graphqlVariablesTemplateObject() {
      if (!this.endpointParentIsGraphql) return {}
      if (this.endpointForm.graphql_variables_mode === 'json') {
        const raw = (this.endpointForm.graphql_variables_json || '').trim()
        if (!raw) return {}
        const parsed = JSON.parse(raw)
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new Error('Variables JSON deve ser um objeto JSON (não array).')
        }
        return parsed
      }
      const variables = {}
      for (const r of nonemptyParamRows(this.endpointForm.parameter_schema)) {
        const n = String(r.name).trim()
        const val = String(r.test_value ?? '').trim()
        if (!val) continue
        this.deepAssignBodyPreview(variables, n, this.coercePreviewValue(val, r.type || 'string'))
      }
      return variables
    },
    async loadApis() {
      this.loading = true
      this.loadError = ''
      const { get } = useApi()
      const { data, error } = await get('/api/v1/api_connectors')
      this.loading = false
      if (error) {
        this.loadError = error.response?.data?.errors?.join?.(', ') || 'Erro ao carregar APIs.'
        return
      }
      this.apis = data.api_connectors || []
    },
    async toggleExpand(id) {
      if (this.expandedId === id) {
        this.expandedId = null
        this.detail = null
        return
      }
      this.expandedId = id
      this.detailLoading = true
      this.detail = null
      const { get } = useApi()
      const { data, error } = await get(`/api/v1/api_connectors/${id}`)
      this.detailLoading = false
      if (error || !data?.api_connector) {
        this.loadError = 'Erro ao carregar detalhes da API.'
        return
      }
      this.detail = data.api_connector
    },
    async openApiModal(apiId) {
      this.apiSaveConfirmOpen = false
      this.apiModalError = ''
      this.editingApiId = apiId

      if (!apiId) {
        this.apiModalLoading = false
        this.apiForm = {
          name: '',
          base_url: '',
          api_kind: 'rest',
          bearer_token: '',
          clear_bearer_token: false,
          active: true
        }
        this.apiFormTokenConfigured = false
        this.apiModalOpen = true
        return
      }

      this.apiForm = {
        name: '',
        base_url: '',
        api_kind: 'rest',
        bearer_token: '',
        clear_bearer_token: false,
        active: true
      }
      this.apiFormTokenConfigured = false
      this.apiModalLoading = true
      this.apiModalOpen = true

      const { get } = useApi()
      const { data, error } = await get(`/api/v1/api_connectors/${apiId}`)
      this.apiModalLoading = false

      if (!this.apiModalOpen || this.editingApiId !== apiId) return

      if (error || !data?.api_connector) {
        const msg = error?.response?.data?.errors
        this.apiModalError = Array.isArray(msg)
          ? msg.join(', ')
          : error?.response?.data?.error || 'Não foi possível carregar a API. Tente novamente.'
        return
      }

      const c = data.api_connector
      this.apiForm = {
        name: c.name || '',
        base_url: c.base_url || '',
        api_kind: c.api_kind || 'rest',
        bearer_token: '',
        clear_bearer_token: false,
        active: c.active !== false
      }
      this.apiFormTokenConfigured = !!c.bearer_token_configured

      if (this.expandedId === apiId) {
        this.detail = c
      }
    },
    dismissApiModalError() {
      this.apiModalError = ''
    },
    dismissEndpointModalError() {
      this.endpointModalError = ''
    },
    dismissEndpointTestError() {
      this.endpointTestError = ''
    },

    closeApiModal() {
      this.apiModalOpen = false
      this.apiModalLoading = false
      this.apiSaveConfirmOpen = false
      this.apiDeleteConfirm = null
      this.apiDeleting = false
      this.apiModalError = ''
    },
    openApiSaveConfirm() {
      if (this.apiModalLoading) return
      this.apiSaveConfirmOpen = true
    },
    closeApiSaveConfirm() {
      this.apiSaveConfirmOpen = false
    },
    async confirmApiSave() {
      this.apiSaveConfirmOpen = false
      await this.saveApi()
    },
    openApiDeleteConfirm(api) {
      this.apiDeleteConfirm = api
    },
    closeApiDeleteConfirm() {
      this.apiDeleteConfirm = null
      this.apiDeleting = false
    },
    async executeApiDelete() {
      if (!this.apiDeleteConfirm) return
      const id = this.apiDeleteConfirm.id
      this.apiDeleting = true
      const ok = await this.deleteApi(id)
      this.apiDeleting = false
      if (ok) this.closeApiDeleteConfirm()
    },
    async saveApi() {
      this.apiSaving = true
      this.apiModalError = ''
      const { post, put } = useApi()
      const payload = {
        api_connector: {
          name: this.apiForm.name.trim(),
          base_url: this.apiForm.base_url.trim(),
          api_kind: this.apiForm.api_kind,
          active: this.apiForm.active,
          clear_bearer_token: this.apiForm.clear_bearer_token
        }
      }
      if (this.apiForm.bearer_token?.trim()) {
        payload.api_connector.bearer_token = this.apiForm.bearer_token.trim()
      }
      let res
      if (this.editingApiId) {
        res = await put(`/api/v1/api_connectors/${this.editingApiId}`, payload)
      } else {
        res = await post('/api/v1/api_connectors', payload)
      }
      this.apiSaving = false
      if (res.error) {
        const err = res.error.response?.data?.errors
        this.apiModalError = Array.isArray(err) ? err.join(', ') : err || 'Erro ao salvar.'
        return
      }
      this.closeApiModal()
      await this.loadApis()
      await this.refreshDetailIfOpen()
    },
    async deleteApi(id) {
      const { delete: del } = useApi()
      const { error } = await del(`/api/v1/api_connectors/${id}`)
      if (error) {
        this.loadError = 'Erro ao excluir API.'
        return false
      }
      if (this.expandedId === id) {
        this.expandedId = null
        this.detail = null
      }
      this.apis = this.apis.filter((a) => String(a.id) !== String(id))
      return true
    },
    emptyEndpointForm() {
      return {
        name: '',
        operation_key: '',
        http_method: 'GET',
        endpoint_intent: 'read',
        path_template: '',
        graphql_query: '',
        graphql_operation_name: '',
        graphql_variables_mode: 'rows',
        graphql_variables_json: '',
        graphql_variables_field_schema_json: defaultGraphqlFieldSchemaJson(),
        prune_empty_fields: true,
        description: '',
        llm_instructions: '',
        parameter_schema: [emptyParamRow()]
      }
    },
    applyEndpointFromShow(ep) {
      const gqlVars = ep?.graphql_variables_template && typeof ep.graphql_variables_template === 'object' ? ep.graphql_variables_template : {}
      const modeStr = String(ep?.graphql_variables_mode || '').toLowerCase()
      const gqlMode = modeStr === 'json' ? 'json' : 'rows'
      this.endpointForm = {
        name: ep?.name || '',
        operation_key: ep?.operation_key || '',
        http_method: ep?.http_method || 'GET',
        endpoint_intent: ep?.endpoint_intent || 'read',
        path_template: ep?.path_template || '',
        graphql_query: ep?.graphql_query || '',
        graphql_operation_name: ep?.graphql_operation_name || '',
        graphql_variables_mode: gqlMode,
        graphql_variables_json: Object.keys(gqlVars).length > 0 ? JSON.stringify(gqlVars, null, 2) : '',
        graphql_variables_field_schema_json: formatGraphqlFieldSchemaJsonForForm(ep?.graphql_variables_field_schema),
        prune_empty_fields: ep?.prune_empty_fields !== false,
        description: ep?.description || '',
        llm_instructions: ep?.llm_instructions || '',
        parameter_schema: (ep?.parameter_schema || []).length
          ? ep.parameter_schema.map((p) => ({
              name: p.name || '',
              in: p.in || (this.endpointParentIsGraphql ? 'body' : 'path'),
              type: p.type || 'string',
              required: !!p.required,
              fixed: !!p.fixed,
              test_value: p.test_value != null && p.test_value !== undefined ? String(p.test_value) : ''
            }))
          : [emptyParamRow()]
      }
    },
    async openEndpointModal(apiId, endpointId) {
      this.endpointSaveConfirmOpen = false
      this.endpointModalError = ''
      this.endpointTestError = ''
      this.endpointTestResult = null
      this.endpointParentApiId = apiId
      this.editingEndpointId = endpointId

      if (!endpointId) {
        this.endpointModalLoading = false
        this.endpointForm = this.emptyEndpointForm()
        if (this.endpointParentIsGraphql) {
          this.endpointForm.http_method = 'POST'
          this.endpointForm.endpoint_intent = 'read'
          this.endpointForm.parameter_schema = [emptyParamRow()].map((r) => ({ ...r, in: 'body' }))
        }
        this.endpointModalOpen = true
        return
      }

      this.endpointForm = this.emptyEndpointForm()
      this.endpointModalLoading = true
      this.endpointModalOpen = true

      const { get } = useApi()
      const { data, error } = await get(`/api/v1/api_connectors/${apiId}/endpoints/${endpointId}`)
      this.endpointModalLoading = false

      if (!this.endpointModalOpen || this.editingEndpointId !== endpointId || this.endpointParentApiId !== apiId) return

      if (error || !data?.endpoint) {
        const msg = error?.response?.data?.errors
        this.endpointModalError = Array.isArray(msg)
          ? msg.join(', ')
          : error?.response?.data?.error || 'Não foi possível carregar o endpoint. Tente novamente.'
        return
      }

      const ep = data.endpoint
      this.applyEndpointFromShow(ep)

      if (this.expandedId === apiId && this.detail?.endpoints) {
        const i = this.detail.endpoints.findIndex((e) => e.id === endpointId)
        if (i >= 0) this.detail.endpoints.splice(i, 1, ep)
      }
    },
    closeEndpointModal() {
      this.endpointModalOpen = false
      this.endpointModalLoading = false
      this.endpointSaveConfirmOpen = false
      this.endpointDeleteConfirm = null
      this.endpointDeleting = false
      this.endpointTestResult = null
      this.endpointTestError = ''
      this.endpointModalError = ''
    },
    openEndpointSaveConfirm() {
      if (this.endpointModalLoading) return
      this.endpointSaveConfirmOpen = true
    },
    closeEndpointSaveConfirm() {
      this.endpointSaveConfirmOpen = false
    },
    async confirmEndpointSave() {
      this.endpointSaveConfirmOpen = false
      await this.saveEndpoint()
    },
    openEndpointDeleteConfirm(apiId, ep) {
      this.endpointDeleteConfirm = { apiId, ep }
    },
    closeEndpointDeleteConfirm() {
      this.endpointDeleteConfirm = null
      this.endpointDeleting = false
    },
    async executeEndpointDelete() {
      if (!this.endpointDeleteConfirm) return
      const { apiId, ep } = this.endpointDeleteConfirm
      this.endpointDeleting = true
      const ok = await this.deleteEndpoint(apiId, ep.id)
      this.endpointDeleting = false
      if (ok) this.closeEndpointDeleteConfirm()
    },
    formatEndpointTestBody(result) {
      if (!result) return ''
      if (result.body_json != null) {
        try {
          return JSON.stringify(result.body_json, null, 2)
        } catch {
          return result.body_preview || ''
        }
      }
      return result.body_preview || ''
    },
    async testEndpoint() {
      this.endpointTestError = ''
      this.endpointTestResult = null
      if (!this.endpointParentApiId) {
        this.endpointTestError = 'API em falta.'
        return
      }
      if (!this.endpointParentIsGraphql && !this.endpointForm.path_template?.trim()) {
        this.endpointTestError = 'Indique o path para testar.'
        return
      }
      if (this.endpointParentIsGraphql && !this.endpointForm.graphql_query?.trim()) {
        this.endpointTestError = 'Indique a query/mutation GraphQL para testar.'
        return
      }
      let graphqlVariablesTemplate = {}
      if (this.endpointParentIsGraphql) {
        try {
          graphqlVariablesTemplate = this.graphqlVariablesTemplateObject()
        } catch (e) {
          this.endpointTestError = e?.message || 'Variables JSON inválido.'
          return
        }
      }
      let graphqlVariablesFieldSchema = {}
      const rawFsTest = (this.endpointForm.graphql_variables_field_schema_json || '').trim()
      if (rawFsTest) {
        try {
          const parsed = JSON.parse(rawFsTest)
          if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
            throw new Error('Schema dos parâmetros deve ser um objecto JSON.')
          }
          graphqlVariablesFieldSchema = parsed
        } catch (e) {
          this.endpointTestError =
            e instanceof SyntaxError
              ? 'Schema dos parâmetros JSON inválido (sintaxe).'
              : e?.message || 'Schema dos parâmetros JSON inválido.'
          return
        }
      }
      this.endpointTestLoading = true
      const { post } = useApi()
      const schema = (this.endpointForm.parameter_schema || [])
        .filter((r) => r.name && String(r.name).trim())
        .map((r) => {
          const row = {
            name: r.name.trim(),
            in: this.endpointParentIsGraphql ? 'body' : r.in,
            type: r.type || 'string',
            required: !!r.required
          }
          if (r.fixed) row.fixed = true
          return row
        })
      const testValues = {}
      for (const r of this.endpointForm.parameter_schema || []) {
        const n = r.name?.trim()
        if (!n) continue
        testValues[n] = r.test_value ?? ''
      }
      const payload = {
        test: {
          http_method: this.endpointForm.http_method,
          path_template: this.endpointForm.path_template.trim(),
          graphql_query: this.endpointForm.graphql_query.trim(),
          graphql_operation_name: this.endpointForm.graphql_operation_name.trim(),
          graphql_variables_mode: this.endpointParentIsGraphql ? this.endpointForm.graphql_variables_mode : 'rows',
          prune_empty_fields: !!this.endpointForm.prune_empty_fields,
          graphql_variables_template: graphqlVariablesTemplate,
          graphql_variables_field_schema: graphqlVariablesFieldSchema,
          parameter_schema: schema,
          test_values: testValues
        }
      }
      const res = await post(`/api/v1/api_connectors/${this.endpointParentApiId}/endpoints/test_run`, payload)
      this.endpointTestLoading = false
      if (res.error) {
        const d = res.error.response?.data
        this.endpointTestError = d?.error || (Array.isArray(d?.errors) ? d.errors.join(', ') : null) || 'Erro ao testar.'
        return
      }
      const data = res.data
      if (data?.ok === false && data?.error) {
        this.endpointTestError = data.error
        return
      }
      this.endpointTestResult = data
    },
    addParamRow() {
      this.endpointForm.parameter_schema.push(emptyParamRow())
    },
    removeParamRow(idx) {
      this.endpointForm.parameter_schema.splice(idx, 1)
      if (this.endpointForm.parameter_schema.length === 0) this.endpointForm.parameter_schema.push(emptyParamRow())
    },
    async saveEndpoint() {
      this.endpointSaving = true
      this.endpointModalError = ''
      let graphqlVariablesTemplate = {}
      if (this.endpointParentIsGraphql) {
        try {
          graphqlVariablesTemplate = this.graphqlVariablesTemplateObject()
        } catch (e) {
          this.endpointSaving = false
          this.endpointModalError = e?.message || 'Variables JSON inválido.'
          return
        }
      }
      let graphqlVariablesFieldSchema = {}
      const rawFs = (this.endpointForm.graphql_variables_field_schema_json || '').trim()
      if (rawFs) {
        try {
          const parsed = JSON.parse(rawFs)
          if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
            throw new Error('Schema dos parâmetros deve ser um objecto JSON.')
          }
          graphqlVariablesFieldSchema = parsed
        } catch (e) {
          this.endpointSaving = false
          this.endpointModalError =
            e instanceof SyntaxError
              ? 'Schema dos parâmetros JSON inválido (sintaxe).'
              : e?.message || 'Schema dos parâmetros JSON inválido.'
          return
        }
      }
      const { post, patch } = useApi()
      const schema = (this.endpointForm.parameter_schema || [])
        .filter((r) => r.name && String(r.name).trim())
        .map((r) => {
          const row = {
            name: r.name.trim(),
            in: this.endpointParentIsGraphql ? 'body' : r.in,
            type: r.type || 'string',
            required: !!r.required
          }
          if (r.fixed) row.fixed = true
          const tv = String(r.test_value ?? '').trim()
          if (tv) row.test_value = tv
          return row
        })
      const payload = {
        endpoint: {
          name: this.endpointForm.name.trim(),
          operation_key: this.endpointForm.operation_key.trim(),
          http_method: this.endpointParentIsGraphql ? 'POST' : this.endpointForm.http_method,
          endpoint_intent:
            this.endpointParentIsGraphql || this.endpointForm.http_method === 'GET'
              ? 'read'
              : this.endpointForm.endpoint_intent,
          path_template: this.endpointParentIsGraphql ? '' : this.endpointForm.path_template.trim(),
          graphql_query: this.endpointParentIsGraphql ? this.endpointForm.graphql_query.trim() : null,
          graphql_operation_name: this.endpointParentIsGraphql ? this.endpointForm.graphql_operation_name.trim() || null : null,
          graphql_variables_mode: this.endpointParentIsGraphql ? this.endpointForm.graphql_variables_mode : 'rows',
          graphql_variables_template: this.endpointParentIsGraphql ? graphqlVariablesTemplate : {},
          graphql_variables_field_schema: graphqlVariablesFieldSchema,
          prune_empty_fields: !!this.endpointForm.prune_empty_fields,
          description: this.endpointForm.description.trim() || null,
          llm_instructions: this.endpointForm.llm_instructions.trim() || null,
          parameter_schema: schema
        }
      }
      let res
      if (this.editingEndpointId) {
        res = await patch(
          `/api/v1/api_connectors/${this.endpointParentApiId}/endpoints/${this.editingEndpointId}`,
          payload
        )
      } else {
        res = await post(`/api/v1/api_connectors/${this.endpointParentApiId}/endpoints`, payload)
      }
      this.endpointSaving = false
      if (res.error) {
        const err = res.error.response?.data?.errors
        this.endpointModalError = Array.isArray(err) ? err.join(', ') : err || 'Erro ao salvar endpoint.'
        return
      }
      this.closeEndpointModal()
      await this.loadApis()
      await this.refreshDetailIfOpen()
    },
    async deleteEndpoint(apiId, endpointId) {
      const { delete: del } = useApi()
      const { error } = await del(`/api/v1/api_connectors/${apiId}/endpoints/${endpointId}`)
      if (error) {
        this.loadError = 'Erro ao excluir endpoint.'
        return false
      }
      const summary = this.apis.find((a) => String(a.id) === String(apiId))
      if (summary && typeof summary.endpoints_count === 'number' && summary.endpoints_count > 0) {
        summary.endpoints_count -= 1
      }
      if (this.expandedId === apiId && this.detail?.endpoints?.length) {
        this.detail.endpoints = this.detail.endpoints.filter((e) => String(e.id) !== String(endpointId))
      }
      return true
    },
    async refreshDetailIfOpen() {
      if (!this.expandedId) return
      this.detailLoading = true
      const { get } = useApi()
      const { data, error } = await get(`/api/v1/api_connectors/${this.expandedId}`)
      this.detailLoading = false
      if (!error && data?.api_connector) this.detail = data.api_connector
    }
  }
}
</script>
